import { Box, styled } from '@mui/material'
import React from 'react'
import PropTypes from 'prop-types'
import StrapiLink from './Link'
import StrapiCountdown from './Countdown'

const SlideContainer = styled('div')(({ theme, isIconOnTop, color, Alignment, hasCountdown }) => ({
  position: 'relative',
  height: '100%',
  display: 'flex',
  justifyContent: Alignment === 'Left' ? 'flex-start' : 'center',
  alignItems: 'center',
  gap: '20px',
  ...(Alignment === 'Left' && {
    paddingLeft: '20px',
    paddingRight: '20px',
  }),
  color,
  [theme.breakpoints.down('md')]: {
    flexDirection: isIconOnTop && 'column',
    gap: isIconOnTop && '0',
    justifyContent: Alignment === 'Left' ? 'flex-start' : 'center',
    paddingRight: Alignment === 'Left' ? '0' : '30px',
  },
  ...(isIconOnTop && {
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      gap: '20px',
    },
  }),
  ...(isIconOnTop &&
    hasCountdown &&
    Alignment !== 'Left' && {
      [theme.breakpoints.up('sm')]: {
        gap: '20px',
      },
    }),
}))

const InfoContainer = styled(Box)(({ theme, isIconOnTop, Alignment, hasCountdown, isMobile }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: '8px',
  alignItems: 'center',
  maxWidth: isIconOnTop || Alignment === 'Left' ? '100%' : '70%',
  [theme.breakpoints.down('md')]: {
    gap: isMobile && hasCountdown ? '0' : '8px',
    ...(!isIconOnTop &&
      Alignment !== 'Left' && {
        marginRight: '30px',
      }),
    ...(hasCountdown && {
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: Alignment !== 'Left' && 'center',
      ...(Alignment === 'Centered' && {
        justifyContent: 'center',
      }),
    }),
  },
  [theme.breakpoints.up('sm')]: {
    marginRight: 0,
  },
  ...(hasCountdown && {
    [theme.breakpoints.up('lg')]: {
      gap: '20px',
    },
  }),
}))

const StyledHeader = styled('h3')(({ theme, hasCountdown, isMobile, color }) => ({
  fontWeight: 600,
  fontSize: '16px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color,
  width: 'auto',
  [theme.breakpoints.down('md')]: {
    maxWidth: isMobile && hasCountdown ? 'fit-content' : '100%',
    fontSize: '14px',
    ...(isMobile &&
      hasCountdown && {
        paddingRight: '10px',
      }),
  },
}))

const SecondaryLinkComponent = styled(StrapiLink)(({ theme, color, removeUnderline, isMobile, hasCountdown }) => ({
  height: '100%',
  fontWeight: 400,
  fontSize: '14px',
  textDecoration: removeUnderline ? 'none' : 'underline',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color,
  zIndex: 1,
  '&:hover': {
    textDecoration: removeUnderline ? 'none' : 'underline',
  },
  '&:active': {
    backgroundColor: 'transparent',
  },
  [theme.breakpoints.down('md')]: {
    ...(isMobile &&
      hasCountdown && {
        paddingRight: '10px',
      }),
  },
}))

const MainInfoLink = styled(StrapiLink)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
})

const TopBarInfoSlide = ({ data, isMobile, isActive, hasActiveCountdown }) => {
  const {
    TextColor,
    Icon,
    Header,
    Title,
    DisableUnderline,
    InfoBarLink,
    SecondaryLink,
    Variant,
    Countdown,
    Alignment,
    // CountdownFontSize,
  } = data
  const variant = Variant || 'IconAdjacent'
  const removeUnderline = !!DisableUnderline
  const textColor = TextColor?.ColorHex || '#ffffff'
  const hasIcon = !!Icon?.url
  const isIconOnTop = variant === 'IconOnTop' && isMobile
  const hasCountdown = !!Countdown?.Countdown
  const accessibilityMessage = Countdown?.Countdown?.AccessibilityMessage
  const expirationDate = Countdown?.Countdown?.ExpirationDate

  const formatExpirationDate = dateString => {
    if (!dateString) return ''

    const date = new Date(dateString)

    // Check if the date is valid
    if (Number.isNaN(date.getTime())) return dateString

    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    })
  }

  return (
    <SlideContainer color={textColor} isIconOnTop={isIconOnTop} Alignment={Alignment} hasCountdown={hasCountdown}>
      {Icon?.url && (
        <img
          style={{ height: isMobile ? '40%' : '45%' }}
          src={`${data.Icon.url}&fm=webp`}
          alt={`${Header || Title || ''} Icon`}
          role="presentation"
          aria-hidden="true"
        />
      )}
      <InfoContainer
        isIconOnTop={isIconOnTop}
        Alignment={Alignment}
        aria-hidden={!!Countdown?.Countdown}
        hasCountdown={hasCountdown}
        isMobile={isMobile}
      >
        {Header && (
          <StyledHeader hasCountdown={hasCountdown} isMobile={isMobile} color={textColor}>
            {Header}
          </StyledHeader>
        )}
        {SecondaryLink?.Link && (
          <SecondaryLinkComponent
            removeUnderline={removeUnderline}
            color={textColor}
            data={SecondaryLink?.Link}
            tabIndex={isActive ? 0 : -1}
            isMobile={isMobile}
            hasCountdown={hasCountdown}
          />
        )}
        {Countdown?.Countdown && (
          <StrapiCountdown
            data={Countdown?.Countdown}
            topBarTextColor={textColor}
            isTopBar
            // CountdownFontSize={CountdownFontSize || 'Small'}
          />
        )}
        <MainInfoLink data={InfoBarLink?.Link} disableUnderline tabIndex={isActive ? 0 : -1}>
          <div style={{ display: 'none' }} />
        </MainInfoLink>
      </InfoContainer>
      {accessibilityMessage && expirationDate && hasActiveCountdown && (
        <span className="hide508">{`${accessibilityMessage} ${formatExpirationDate(expirationDate)}`}</span>
      )}
    </SlideContainer>
  )
}

TopBarInfoSlide.propTypes = {
  data: PropTypes.shape({
    TextColor: PropTypes.shape({
      ColorHex: PropTypes.string.isRequired,
    }).isRequired,
    Icon: PropTypes.shape({
      alternativeText: PropTypes.string,
      height: PropTypes.number,
      id: PropTypes.string,
      mime: PropTypes.string,
      url: PropTypes.string,
      width: PropTypes.number,
    }),
    Header: PropTypes.string,
    Title: PropTypes.string,
    DisableUnderline: PropTypes.bool,
    InfoBarLink: PropTypes.shape({
      Link: PropTypes.shape({
        AlternateDescription: PropTypes.string,
        Color: PropTypes.string,
        CustomLinkGrowthbookId: PropTypes.string,
        DisplayText: PropTypes.string,
        ExternalUrl: PropTypes.string,
        InternalUrl: PropTypes.string,
        MobileDisplayText: PropTypes.string,
        PredefinedComponentModal: PropTypes.string,
        PredefinedLink: PropTypes.string,
        ShowLearnMoreFinanceModal: PropTypes.string,
        Target: PropTypes.string,
        Title: PropTypes.string,
        id: PropTypes.string,
        testId: PropTypes.string,
      }),
    }),
    SecondaryLink: PropTypes.shape({
      Link: PropTypes.shape({
        AlternateDescription: PropTypes.string,
        Color: PropTypes.string,
        CustomLinkGrowthbookId: PropTypes.string,
        DisplayText: PropTypes.string,
        ExternalUrl: PropTypes.string,
        InternalUrl: PropTypes.string,
        MobileDisplayText: PropTypes.string,
        PredefinedComponentModal: PropTypes.string,
        PredefinedLink: PropTypes.string,
        ShowLearnMoreFinanceModal: PropTypes.string,
        Target: PropTypes.string,
        Title: PropTypes.string,
        id: PropTypes.string,
        testId: PropTypes.string,
      }),
    }),
    Variant: PropTypes.string,
    Countdown: PropTypes.shape({
      Countdown: PropTypes.shape({
        AccessibilityMessage: PropTypes.string,
        ExpirationDate: PropTypes.string,
        Title: PropTypes.string,
        BackgroundColor: PropTypes.shape({
          ColorHex: PropTypes.string,
        }),
      }),
    }),
    Alignment: PropTypes.string,
    // CountdownFontSize: PropTypes.string,
  }).isRequired,
  isMobile: PropTypes.bool,
  isActive: PropTypes.bool,
  hasActiveCountdown: PropTypes.bool,
}

export default TopBarInfoSlide
